import {
  AUTH_GREEN,
  AUTH_GREEN_BORDER,
  AUTH_GREEN_HOVER,
  AUTH_GREEN_LIGHT,
} from '../../user-dashboard/auth/authTheme';
import type { WhatsNewActiveItem } from '../../../services/whatsNewService';

interface WhatsNewModalProps {
  open: boolean;
  item: WhatsNewActiveItem | null;
  dismissing?: boolean;
  onDismiss: () => void;
}

/**
 * Post-login "What's New" modal — Play Store style release notes.
 * Content is authored by admins (no code deploy required).
 */
export default function WhatsNewModal({
  open,
  item,
  dismissing = false,
  onDismiss,
}: WhatsNewModalProps) {
  if (!open || !item) return null;

  return (
    <div
      className="fixed inset-0 z-[270] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-heading"
    >
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        aria-hidden
        onClick={onDismiss}
      />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-black/5">
        <div
          className="px-5 pt-5 pb-4 border-b"
          style={{ background: AUTH_GREEN_LIGHT, borderColor: AUTH_GREEN_BORDER }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white"
              style={{ background: AUTH_GREEN }}
            >
              <i className="ri-megaphone-line text-xl" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900/70">
                What&apos;s New
                {item.version_label ? ` · ${item.version_label}` : ''}
              </p>
              <h2 id="whats-new-heading" className="text-lg font-bold text-gray-900 leading-snug">
                {item.title}
              </h2>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 max-h-[50vh] overflow-y-auto">
          {item.bullets.length > 0 ? (
            <ul className="space-y-2.5">
              {item.bullets.map((bullet, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-gray-700 leading-snug">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: AUTH_GREEN }}
                    aria-hidden
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No details for this update.</p>
          )}
        </div>

        <div className="px-5 pb-5">
          <button
            type="button"
            disabled={dismissing}
            onClick={onDismiss}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors disabled:opacity-60"
            style={{ background: AUTH_GREEN }}
            onMouseEnter={(e) => {
              if (!dismissing) e.currentTarget.style.background = AUTH_GREEN_HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = AUTH_GREEN;
            }}
          >
            {dismissing ? 'Saving…' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}
